import process from 'node:process';
import { hasFlag } from '../auth.js';
import { apiRequest } from '../cli-helpers.js';

type ArmComparisonRow = {
  arm: string;
  workflowName: string;
  runNumber: number;
  isCached: boolean;
  status: string;
  durationMs: number | null;
  totalTokens: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  successfulNodes: number | null;
  failedNodes: number | null;
  totalNodes: number | null;
};

type ArmAgg = {
  arm: string;
  cached: boolean;
  runs: number;
  succeeded: number;
  durationSum: number;
  durationN: number;
  tokenSum: number;
  tokenN: number;
  nodePass: number;
  nodeTotal: number;
};

function aggregateArms(rows: ArmComparisonRow[]): ArmAgg[] {
  const byKey = new Map<string, ArmAgg>();
  for (const r of rows) {
    const key = `${r.arm}\x00${r.isCached}`;
    let agg = byKey.get(key);
    if (!agg) {
      agg = {
        arm: r.arm || '(default)',
        cached: r.isCached,
        runs: 0,
        succeeded: 0,
        durationSum: 0,
        durationN: 0,
        tokenSum: 0,
        tokenN: 0,
        nodePass: 0,
        nodeTotal: 0,
      };
      byKey.set(key, agg);
    }
    agg.runs++;
    if (r.status === 'completed') agg.succeeded++;
    if (typeof r.durationMs === 'number') {
      agg.durationSum += r.durationMs;
      agg.durationN++;
    }
    if (typeof r.totalTokens === 'number') {
      agg.tokenSum += r.totalTokens;
      agg.tokenN++;
    }
    if (typeof r.successfulNodes === 'number') agg.nodePass += r.successfulNodes;
    if (typeof r.totalNodes === 'number') agg.nodeTotal += r.totalNodes;
  }
  // Uncached (model-quality) rows first, then cached (cache-quality) rows.
  return [...byKey.values()].sort(
    (a, b) => Number(a.cached) - Number(b.cached) || a.arm.localeCompare(b.arm)
  );
}

function pct(n: number, d: number): string {
  return d === 0 ? '—' : `${Math.round((100 * n) / d)}%`;
}

export async function handleReport(argv: string[], apiUrl: string, token: string): Promise<void> {
  const suiteId = argv.find((a) => !a.startsWith('--'));
  if (!suiteId) {
    console.error('Error: benchmark report <suiteId>');
    process.exit(1);
  }
  const res = await apiRequest<{
    ok: boolean;
    error?: string;
    run?: { id: string; status: string };
    rows?: ArmComparisonRow[];
  }>(apiUrl, token, 'GET', `/superadmin/benchmark/workflow-suites/${suiteId}/arm-report`);

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }
  const rows = res.rows ?? [];
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify({ run: res.run, rows }, null, 2));
    return;
  }

  console.log(`Suite ${res.run?.id ?? suiteId} [${res.run?.status ?? '?'}] — ${rows.length} run rows`);
  if (rows.length === 0) {
    console.log('  (no rows yet — the run may still be starting)');
    return;
  }

  const aggs = aggregateArms(rows);
  const header = ['arm', 'phase', 'runs', 'success', 'nodePass', 'avgMs', 'avgTokens'];
  console.log('  ' + header.join('\t'));
  for (const a of aggs) {
    const avgMs = a.durationN === 0 ? '—' : Math.round(a.durationSum / a.durationN).toString();
    const avgTok = a.tokenN === 0 ? '—' : Math.round(a.tokenSum / a.tokenN).toString();
    console.log(
      '  ' +
        [
          a.arm,
          a.cached ? 'cached' : 'uncached',
          a.runs,
          `${pct(a.succeeded, a.runs)} (${a.succeeded}/${a.runs})`,
          pct(a.nodePass, a.nodeTotal),
          avgMs,
          avgTok,
        ].join('\t')
    );
  }
  console.log('\nModel quality lives in the uncached rows; cached rows reflect cache quality per arm.');
}
